import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Play, Copy, RotateCcw, Sparkles, FileText, Image, Code, Mail, Presentation, MessageSquare, Lock, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROMPT_TEMPLATES = [
  {
    id: 'summarize',
    icon: FileText,
    title_fr: 'Résumer un texte',
    title_en: 'Summarize a text',
    color: 'bg-blue-50 text-blue-600',
    prompt_fr: 'Résume le texte suivant en 3 points clés :\n\n[Collez votre texte ici]',
    prompt_en: 'Summarize the following text in 3 key points:\n\n[Paste your text here]',
  },
  {
    id: 'email',
    icon: Mail,
    title_fr: 'Rédiger un email pro',
    title_en: 'Write a pro email',
    color: 'bg-orange-50 text-orange-600',
    prompt_fr: 'Rédige un email professionnel pour [objectif]. Ton : [formel/amical]. Contexte : [détails].',
    prompt_en: 'Write a professional email for [objective]. Tone: [formal/friendly]. Context: [details].',
  },
  {
    id: 'brainstorm',
    icon: Sparkles,
    title_fr: 'Brainstorming d\'idées',
    title_en: 'Idea brainstorming',
    color: 'bg-emerald-50 text-emerald-600',
    prompt_fr: 'Génère 10 idées créatives pour [sujet]. Pense de façon originale et innovante.',
    prompt_en: 'Generate 10 creative ideas for [topic]. Think outside the box.',
  },
  {
    id: 'code',
    icon: Code,
    title_fr: 'Générer du code',
    title_en: 'Generate code',
    color: 'bg-violet-50 text-violet-600',
    prompt_fr: 'Écris un code [langage] qui fait [description]. Ajoute des commentaires explicatifs.',
    prompt_en: 'Write [language] code that does [description]. Add explanatory comments.',
  },
  {
    id: 'presentation',
    icon: Presentation,
    title_fr: 'Plan de présentation',
    title_en: 'Presentation outline',
    color: 'bg-amber-50 text-amber-600',
    prompt_fr: 'Crée un plan de présentation de 10 slides sur [sujet] pour [audience]. Inclus des titres accrocheurs.',
    prompt_en: 'Create a 10-slide presentation outline on [topic] for [audience]. Include catchy titles.',
  },
  {
    id: 'analyze',
    icon: MessageSquare,
    title_fr: 'Analyser des données',
    title_en: 'Analyze data',
    color: 'bg-rose-50 text-rose-600',
    prompt_fr: 'Analyse ces données et fournis : 1) Les tendances clés, 2) Les anomalies, 3) Des recommandations.\n\n[Données ici]',
    prompt_en: 'Analyze this data and provide: 1) Key trends, 2) Anomalies, 3) Recommendations.\n\n[Data here]',
  },
];

export default function PlaygroundPage() {
  const { user, getAuthHeaders } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const isPremium = user?.plan === 'pro' || user?.plan === 'entreprise' || user?.plan === 'decouverte';

  const handleSend = async () => {
    if (!prompt.trim() || loading) return;
    setLoading(true);
    setResponse('');
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/chat`, {
        message: prompt,
        session_id: `playground_${user?.user_id || 'anon'}_${Date.now()}`
      }, { headers, withCredentials: true });
      setResponse(res.data.response);
    } catch (err) {
      setResponse(language === 'fr'
        ? 'Erreur : ' + (err.response?.data?.detail || 'Impossible de contacter l\'assistant.')
        : 'Error: ' + (err.response?.data?.detail || 'Unable to contact the assistant.')
      );
    }
    setLoading(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const selectTemplate = (tmpl) => {
    setSelectedTemplate(tmpl.id);
    setPrompt(language === 'fr' ? tmpl.prompt_fr : tmpl.prompt_en);
    setResponse('');
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Lock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Poppins' }}>
            {language === 'fr' ? 'Connectez-vous pour accéder au Playground' : 'Login to access the Playground'}
          </h2>
          <Button className="mt-4 bg-blue-600 text-white" onClick={() => navigate('/auth')}>
            {language === 'fr' ? 'Se connecter' : 'Login'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8ECF1]" data-testid="playground-page">
      <SEO
        title={language === 'fr' ? 'Playground IA - Testez vos Prompts' : 'AI Playground - Test Your Prompts'}
        description={language === 'fr'
          ? "Testez vos prompts IA en temps réel avec l'assistant Claude. 6 templates guidés : résumé, email, brainstorming, code, présentation, analyse."
          : "Test your AI prompts in real time with Claude assistant. 6 guided templates: summary, email, brainstorming, code, presentation, analysis."}
        path="/playground"
        noIndex
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-16 h-16 rounded-xl" data-testid="playground-logo" />
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Poppins' }} data-testid="playground-title">
              {language === 'fr' ? 'Playground IA' : 'AI Playground'}
            </h1>
            <p className="text-sm text-slate-500">
              {language === 'fr' ? 'Testez l\'IA avec des prompts guidés. Apprenez en expérimentant.' : 'Test AI with guided prompts. Learn by experimenting.'}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Templates sidebar */}
          <div className="lg:col-span-1">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
              {language === 'fr' ? 'Prompts guidés' : 'Guided prompts'}
            </h2>
            <div className="space-y-2">
              {PROMPT_TEMPLATES.map((tmpl) => {
                const Icon = tmpl.icon;
                const active = selectedTemplate === tmpl.id;
                return (
                  <button
                    key={tmpl.id}
                    onClick={() => selectTemplate(tmpl)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all border ${
                      active ? 'border-blue-300 bg-blue-50 shadow-sm' : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                    data-testid={`template-${tmpl.id}`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tmpl.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className={`text-sm font-medium ${active ? 'text-blue-700' : 'text-slate-700'}`}>
                      {language === 'fr' ? tmpl.title_fr : tmpl.title_en}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main area */}
          <div className="lg:col-span-2 space-y-4">
            {/* Input */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {language === 'fr' ? 'Votre prompt' : 'Your prompt'}
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                rows={5}
                placeholder={language === 'fr' ? 'Écrivez votre prompt ici ou sélectionnez un modèle...' : 'Write your prompt here or select a template...'}
                data-testid="playground-input"
              />
              <div className="flex items-center justify-between mt-3">
                <button
                  onClick={() => { setPrompt(''); setResponse(''); setSelectedTemplate(null); }}
                  className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
                  data-testid="playground-clear"
                >
                  <RotateCcw className="w-3 h-3" /> {language === 'fr' ? 'Effacer' : 'Clear'}
                </button>
                {isPremium ? (
                  <Button
                    className="bg-orange-500 text-white hover:bg-orange-600 active:scale-[0.98] transition-all font-semibold"
                    onClick={handleSend}
                    disabled={!prompt.trim() || loading}
                    data-testid="playground-send"
                  >
                    {loading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    {loading
                      ? (language === 'fr' ? 'Génération...' : 'Generating...')
                      : (language === 'fr' ? 'Exécuter' : 'Execute')
                    }
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs text-orange-600 border-orange-200">
                      <Lock className="w-3 h-3 mr-1" /> Premium
                    </Badge>
                    <Button size="sm" className="bg-orange-500 text-white hover:bg-orange-600" onClick={() => navigate('/pricing')}>
                      {language === 'fr' ? 'Débloquer' : 'Unlock'}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Response */}
            {(response || loading) && (
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="playground-response">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-700">
                    {language === 'fr' ? 'Réponse IA' : 'AI Response'}
                  </h3>
                  {response && (
                    <button
                      onClick={handleCopy}
                      className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
                      data-testid="playground-copy"
                    >
                      {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                      {copied ? (language === 'fr' ? 'Copié !' : 'Copied!') : (language === 'fr' ? 'Copier' : 'Copy')}
                    </button>
                  )}
                </div>
                {loading ? (
                  <div className="flex items-center gap-2 py-8 justify-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                  </div>
                ) : (
                  <div className="prose max-w-none text-sm text-slate-700 whitespace-pre-wrap">
                    {response}
                  </div>
                )}
              </div>
            )}

            {/* Tips */}
            {!response && !loading && (
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <h3 className="text-sm font-semibold text-blue-800 mb-2">
                  {language === 'fr' ? 'Conseils pour de meilleurs résultats' : 'Tips for better results'}
                </h3>
                <ul className="space-y-1.5 text-xs text-blue-700">
                  {(language === 'fr'
                    ? ['Soyez précis dans votre demande', 'Donnez du contexte (rôle, audience, format)', 'Utilisez la méthode CREA : Contexte, Rôle, Exemple, Action', 'Itérez : affinez votre prompt après chaque réponse']
                    : ['Be specific in your request', 'Give context (role, audience, format)', 'Use the CREA method: Context, Role, Example, Action', 'Iterate: refine your prompt after each response']
                  ).map((tip, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-3 h-3 mt-0.5 text-blue-500 shrink-0" /> {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
